import { cn } from '@invent/wl-ui-kit-next';
import { Input } from '@invent/wl-ui-kit-next/ui';
import { FlaskConical } from 'lucide-react';
import * as React from 'react';

import { allowedDomainsText, type ITestInbox } from '../mail-delivery';

/**
 * The test inbox (`deliverTo`), drawn so that nobody mistakes it for part of the product.
 *
 * It exists only where survey-api answers `redirect: true` — dev and UAT — so a screenshot of
 * a form carrying it must not read as the form production ships. It is fenced off rather than
 * laid in line with the fields around it: a dashed edge, a wash of the brand's accent yellow,
 * and "Dev / UAT only" as the legend, which is the group's accessible name too — what a screen
 * reader announces on entering it. The copy under the input says the three things a tester
 * who has never seen the field needs before typing: what it does to the mail, when it may be
 * left empty, and that production has no such field.
 *
 * With the mode off it renders nothing but a refusal still standing: `not_accepted` arrives
 * exactly when the re-read it causes takes the field away, and the sentence explaining that
 * has to outlive the input it is about.
 */
export function TestInboxField({
  inbox,
  id,
  intended,
  disabled,
  whenEmpty
}: {
  inbox: ITestInbox;
  id: string;
  /** The address the mail is about — the one typed above, or the account's. */
  intended: string;
  disabled?: boolean;
  /**
   * What leaving it empty does, where that is not a refusal: the registration completion, which
   * survey-api never refuses for an empty inbox and whose mail it withholds instead (its
   * self-service plan, R12). Absent, the hint says when it may be left empty — every other route
   * refuses an empty one `missing` for an address at no allowed domain.
   */
  whenEmpty?: React.ReactNode;
}) {
  const { delivery, value, setValue, error } = inbox;
  const errorId = `${id}-error`;
  const refusal = error ? (
    <p id={errorId} role="alert" className="text-sm font-medium text-destructive">
      {error}
    </p>
  ) : null;

  if (!delivery?.redirect) return refusal;

  const hintId = `${id}-hint`;
  const domains = allowedDomainsText(delivery.allowedDomains);
  const meantFor = intended.trim();

  return (
    <fieldset className="grid gap-2 rounded-md border-2 border-dashed border-[var(--veritant-yellow-edge)] bg-[var(--color-yellow)]/10 px-4 pt-1 pb-4">
      <legend className="px-1">
        <span className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--color-yellow)] px-2 py-0.5 text-xs font-semibold tracking-wide text-[var(--color-midnight-blue)] uppercase">
          <FlaskConical className="size-3.5" aria-hidden />
          Dev / UAT only
        </span>
      </legend>

      <label htmlFor={id} className="text-sm font-medium">
        Test inbox
      </label>
      <Input
        id={id}
        // `text` with the attributes `email` would have carried, for the sign-in field's
        // reason: `type="email"` is a format rule of the browser's, and whether this is one
        // plain address is survey-api's to say (`malformed`).
        type="text"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoComplete="email"
        placeholder={`you@${delivery.allowedDomains[0] ?? ''}`}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? `${hintId} ${errorId}` : hintId}
      />
      <p id={hintId} className="text-sm text-muted-foreground">
        This environment does not mail real addresses. The mail for{' '}
        {meantFor ? <strong className="break-words">{meantFor}</strong> : 'the address above'}{' '}
        goes to this inbox instead, which has to end in {domains}.{' '}
        {whenEmpty ?? 'Leave it empty only if that address ends in one of them itself.'}{' '}
        Production has no such field.
      </p>
      {refusal}
    </fieldset>
  );
}

/**
 * What a test environment did with a mail, said beside the confirmation that reports it — in the
 * field's own colours, because a tester reading "check your inbox" should not go looking in the
 * advisor's. Drawn only where the deployment redirects; the sentence is the caller's.
 */
export function TestInboxNote({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'rounded-md border-2 border-dashed border-[var(--veritant-yellow-edge)]',
        'bg-[var(--color-yellow)]/10 px-3 py-2 text-sm',
        className
      )}
    >
      <span className="font-semibold">Dev / UAT only:</span> {children}
    </p>
  );
}
